"use client";

interface GradientCircleStatsProps {
    value: string;
    title?: string;
    description?: string;
    color?: string;
    textColor?: string;
    textSize?: string;
    className?: string;
    hasGradient?: boolean;
    gradientFrom?: string;
    gradientTo?: string;
    hasGrainyEffect?: boolean;
    showArrow?: boolean;
    horizontalLayout?: boolean;
}

export function GradientCircleStats({
    value,
    title,
    description,
    color = "bg-ss-light-green",
    textColor = "text-white",
    textSize = "text-xl",
    className = "",
    hasGradient = false,
    gradientFrom,
    gradientTo,
    hasGrainyEffect = false,
    showArrow = false,
    horizontalLayout = false
}: GradientCircleStatsProps) {

    // Simple color class generation
    const getColorClass = (colorName: string) => {
        // If it already starts with 'bg-ss-', use it as is
        if (colorName.startsWith('bg-ss-')) {
            return colorName;
        }
        // Otherwise, prepend 'bg-ss-'
        return `bg-ss-${colorName}`;
    };

    const colorClass = getColorClass(color);

    return (
        <div className={`${horizontalLayout ? 'flex flex-col items-center sm:flex-row sm:items-center gap-4' : 'flex flex-col justify-center items-center'} ${className}`}>
            <div className={`relative ${horizontalLayout ? '' : 'mb-3 sm:mb-4 lg:mb-8'}`}>
                {/* Circle with gradient and texture */}
                <div className={`relative ${horizontalLayout ? 'w-20 h-20 sm:w-24 sm:h-24' : 'w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-48 lg:h-48'} ${horizontalLayout ? '' : 'mx-auto'}`}>
                    {/* White circle with arrow - positioned below on mobile, top left on desktop */}
                    {showArrow && (
                        <div className={`${horizontalLayout ? 'w-6 h-6 sm:w-8 sm:h-8' : 'w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20'} bg-white rounded-full flex items-center justify-center shadow-lg z-30 absolute ${
                            // Mobile: Top left of circle on all screen sizes
                            horizontalLayout ? '-top-1 -left-1' : '-top-1 -left-1 sm:-top-2 sm:-left-2'
                        }`}>
                            <svg 
                                width="32" 
                                height="32"
                                className={`${horizontalLayout ? 'w-3 h-3 sm:w-4 sm:h-4' : 'w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12'} text-ss-green`}
                                viewBox="0 0 24 24" 
                                fill="none" 
                            >
                                <path 
                                    d="M4 12L20 12M20 12L14 6M20 12L14 18" 
                                    stroke="currentColor" 
                                    strokeWidth="2.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    )}
                    
                    <div 
                        className={`${horizontalLayout ? 'w-16 h-16 sm:w-20 sm:h-20' : 'w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40'} rounded-full flex items-center justify-center font-bold overflow-hidden absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 ${
                            hasGradient && !gradientFrom && !gradientTo ? '' : colorClass
                        }`}
                        style={{
                            background: hasGradient && gradientFrom && gradientTo 
                                ? `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`
                                : undefined,
                        }}
                    >
                        {/* Grainy texture overlay */}
                        {hasGrainyEffect && (
                            <div 
                                className="absolute inset-0 opacity-40 pointer-events-none rounded-full" 
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                                    backgroundSize: '150px 150px'
                                }}
                            />
                        )}
                        <span className={`relative z-10 text-center leading-tight px-1 text-white ${horizontalLayout ? 'text-sm sm:text-base' : 'text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl'} font-bold`}>
                            {value}
                        </span>
                    </div>
                </div>
            </div>
            
            {(title || description) && (
                <div className={`${horizontalLayout ? 'text-center sm:text-left flex-1' : 'text-center h-auto sm:h-16 md:h-20 lg:h-24 flex flex-col justify-start'} z-20 relative`}>
                    {title && <h3 className={`font-semibold ${horizontalLayout ? 'mb-1' : 'mb-1 sm:mb-2'} capitalize leading-tight text-white ${horizontalLayout ? 'text-sm sm:text-base' : 'text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl'}`}>{title}</h3>}
                    {description && <p className={`text-white ${horizontalLayout ? 'text-xs sm:text-sm' : 'text-[11px] sm:text-xs md:text-sm lg:text-base xl:text-lg'} leading-tight ${horizontalLayout ? '' : 'max-w-[160px] sm:max-w-[200px] md:max-w-[240px] lg:max-w-[280px] xl:max-w-[320px] mx-auto'}`}>{description}</p>}
                </div>
            )}
        </div>
    );
}
